import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Clock } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import { TreatmentIcon } from "./iconMap";

export function TreatmentCard({ treatment, featured = false }) {
  const { t, lang, e } = useI18n();
  const to = e(treatment.slug);
  return (
    <Link
      to={to}
      data-testid={`treatment-card-${treatment.key}`}
      className={`group relative flex flex-col overflow-hidden rounded-3xl bg-white border border-navy/8 shadow-soft transition-all duration-300 hover:shadow-lift hover:-translate-y-1 ${
        featured ? "sm:col-span-2" : ""
      }`}
    >
      {featured && (
        <div className="relative h-48 sm:h-56 overflow-hidden">
          <img
            src={treatment.image}
            alt={`${treatment.name[lang] || treatment.name.en} — ${treatment.tagline[lang] || treatment.tagline.en}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dental/8 text-dental">
          <TreatmentIcon name={treatment.icon} className="h-6 w-6" />
        </span>
        <h3 className="mt-5 text-xl sm:text-2xl text-navy">{treatment.name[lang] || treatment.name.en}</h3>
        <p className="mt-2 text-[15px] text-navy/65 leading-relaxed">
          {treatment.tagline[lang] || treatment.tagline.en}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-dental">
          {t.cta.viewTreatment}
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export function LocationCard({ location }) {
  const { t, lang, e } = useI18n();
  const to = e(location.slug);
  return (
    <Link
      to={to}
      data-testid={`location-card-${location.key}`}
      className="group relative block overflow-hidden rounded-3xl shadow-soft transition-all duration-300 hover:shadow-lift hover:-translate-y-1"
    >
      <div className="relative h-80 sm:h-96 overflow-hidden">
        <img
          src={location.image}
          alt={`${location.name[lang] || location.name.en}, Vietnam`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
        <h3 className="text-2xl sm:text-3xl text-white">{location.name[lang] || location.name.en}</h3>
        <p className="mt-2 text-sm text-white/80 leading-relaxed max-w-xs">
          {location.tagline[lang] || location.tagline.en}
        </p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-turquoise-light">
          {t.cta.exploreCity}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function ArticleCard({ article }) {
  const { t, lang, a } = useI18n();
  const to = a(article.slug);
  return (
    <Link
      to={to}
      data-testid={`article-card-${article.key}`}
      className="group flex flex-col overflow-hidden rounded-3xl bg-white border border-navy/8 shadow-soft transition-all duration-300 hover:shadow-lift hover:-translate-y-1"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={article.image}
          alt={article.title[lang] || article.title.en}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-dental backdrop-blur">
          {article.category[lang] || article.category.en}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="text-xl text-navy leading-snug">{article.title[lang] || article.title.en}</h3>
        <p className="mt-3 text-[15px] text-navy/65 leading-relaxed line-clamp-3">
          {article.excerpt[lang] || article.excerpt.en}
        </p>
        <div className="mt-5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-navy/50">
            <Clock className="h-3.5 w-3.5" />
            {article.readTime} {t.common.readTime}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-dental">
            {t.cta.readGuide}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}
